import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import { Search, LayoutDashboard, Compass, Boxes, Activity, UserCheck, ShieldCheck, Settings as Cog, Play } from "lucide-react";

const ITEMS = [
  { group: "Navigation", label: "Command Center", to: "/", icon: LayoutDashboard, hint: "G then C" },
  { group: "Navigation", label: "AI Discovery", to: "/discovery", icon: Compass, hint: "G then D" },
  { group: "Navigation", label: "AI Inventory", to: "/inventory", icon: Boxes, hint: "G then I" },
  { group: "Navigation", label: "Runtime Monitor", to: "/runtime", icon: Activity, hint: "G then R" },
  { group: "Navigation", label: "Human Oversight", to: "/oversight", icon: UserCheck, hint: "G then H" },
  { group: "Navigation", label: "Audit Center", to: "/audit", icon: ShieldCheck, hint: "G then A" },
  { group: "Navigation", label: "Settings · SSO & SCIM", to: "/settings", icon: Cog, hint: "G then S" },
  { group: "Agents", label: "Applicant Data Agent", to: "/inventory/applicant", icon: Boxes },
  { group: "Agents", label: "Credit Risk Agent", to: "/inventory/credit", icon: Boxes },
  { group: "Agents", label: "Loan Decision Agent", to: "/inventory/decision", icon: Boxes },
  { group: "Agents", label: "Loan Decision · Policy Editor", to: "/inventory/decision/policy", icon: ShieldCheck },
  { group: "Actions", label: "Start Live Demo Tour", to: "?demo=1", icon: Play },
];

export default function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const run = (to) => {
    onOpenChange(false);
    setQ("");
    if (to.startsWith("?")) {
      navigate({ pathname: window.location.pathname, search: to });
    } else navigate(to);
  };

  const groupedItems = useMemo(() => {
    return ["Navigation", "Agents", "Actions"].map((group) => ({
      group,
      items: ITEMS.filter((i) => i.group === group),
    }));
  }, []);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]" data-testid="command-palette">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
      <div className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-white/10 bg-[#0f1730] shadow-2xl">
        <CommandPrimitive
          shouldFilter={true}
          loop
          label="Global command palette"
          className="flex w-full flex-col"
        >
          <div className="flex items-center gap-3 border-b border-white/5 px-4 py-3">
            <Search size={15} className="text-slate-500" />
            <CommandPrimitive.Input
              value={q}
              onValueChange={setQ}
              autoFocus
              placeholder="Search pages, agents, actions…"
              data-testid="palette-input"
              className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
            />
            <kbd className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">ESC</kbd>
          </div>
          <CommandPrimitive.List className="max-h-[60vh] overflow-y-auto p-2">
            <CommandPrimitive.Empty className="px-3 py-6 text-center text-sm text-slate-500">
              No results for “{q}”
            </CommandPrimitive.Empty>
            {groupedItems.map(({ group, items }) => (
              <CommandPrimitive.Group key={group} heading={group}>
                <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{group}</div>
                {items.map((i) => (
                  <CommandPrimitive.Item
                    key={i.label}
                    value={`${i.label} ${i.to}`}
                    onSelect={() => run(i.to)}
                    data-testid={`palette-item-${i.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 text-[13px] text-slate-300 data-[selected=true]:bg-[#5B8DEF]/10 data-[selected=true]:text-white"
                  >
                    <i.icon size={14} className="text-slate-400" />
                    <span className="flex-1">{i.label}</span>
                    {i.hint && <span className="font-mono text-[10px] text-slate-500">{i.hint}</span>}
                  </CommandPrimitive.Item>
                ))}
              </CommandPrimitive.Group>
            ))}
          </CommandPrimitive.List>
          <div className="flex items-center justify-between border-t border-white/5 bg-white/[0.02] px-4 py-2 text-[11px] text-slate-500">
            <span>AI-ECO Suite · Command Palette</span>
            <span className="flex items-center gap-2 font-mono">
              <kbd className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5">↑↓</kbd>
              <kbd className="rounded border border-white/10 bg-white/5 px-1.5 py-0.5">↵</kbd>
            </span>
          </div>
        </CommandPrimitive>
      </div>
    </div>
  );
}
